import * as SentryVue from "@sentry/vue";
import * as SentryCapacitor from "@sentry/capacitor";
import PRODUUZIT_FRONT_ENV from "/src/utils/defaults";
import VERSION from "/src/utils/version";
import { isStaleBuild } from "/src/utils/stale_chunks";

// An aborted axios request is always deliberate: the request interceptor in
// `utils/apiv2.js` aborts every call made after a logout, and
// `ApiConnector.cancel()` aborts the ones a view no longer needs. A caller
// that does not catch it produces an unhandled rejection whose stack holds
// only axios frames, so it can be neither traced nor acted on.
const isCanceledRequest = (e) => e?.code === "ERR_CANCELED" || e?.name === "CanceledError";

// A request that got no response at all: no network, a timeout, a tab put to
// sleep. The response interceptor already tells the user ("Connection lost"),
// and there is nothing in the code to fix.
const isNetworkFailure = (e) => {
    if (e?.isAxiosError && !e.response) return true;
    return e?.name === "TypeError"
        && /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/.test(e.message);
};

// The endpoint of a failed request, without host, query or ids:
// `/api/v2/constructibles/assemblies/:id/part_gids/`.
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const requestEndpoint = (config) => {
    let url = config?.url || "";
    if (config?.baseURL && !/^https?:\/\//.test(url)) url = config.baseURL.replace(/\/$/, "") + "/" + url.replace(/^\//, "");
    const path = url.replace(/^https?:\/\/[^/]+/, "").split("?")[0];
    return path.replace(UUID, ":id").replace(/\/\d+(?=\/|$)/g, "/:id");
};

// Every failed request has the same stack, axios's own frames, so Sentry put
// them all in one issue whatever the endpoint: a 403 in a report form and a
// 404 in the viewer could not be told apart. Group them by endpoint instead,
// and say which one in the title.
const groupAxiosError = (event, e) => {
    if (!e?.isAxiosError || !e.response) return;
    const method = (e.config?.method || "get").toUpperCase();
    const endpoint = requestEndpoint(e.config);
    const status = e.response.status;
    event.fingerprint = ["axios", method, endpoint, String(status)];
    const value = event.exception?.values?.[0];
    if (value) value.value = `${status} ${method} ${endpoint}`;
};

// The camera or a video was stopped by the page or the browser on purpose.
const isAbort = (e) => e?.name === "AbortError"
    || (typeof e === "string" && e.startsWith("AbortError"));

// Errors that only browsers raise for their own scripts.
const IGNORED_MESSAGES = [
    "The WKWebView was deallocated before the message was delivered",
    // pdf.js, when a document is destroyed while it still loads: the viewer
    // moved to another file, which is what destroying it is for.
    "Transport destroyed",
    "Worker was destroyed",
];

// Chrome on iOS injects its own scripts to translate the page. Their errors
// carry frames that point at the page itself (`/app/login:415`) and never at
// our bundle, or no frame at all and a minified name for a message
// (`Error: ga`, `Error: Ba`).
const isForeignScript = (event) => {
    const values = event.exception?.values || [];
    if (values.length === 0) return false;
    const frames = values.flatMap(v => v.stacktrace?.frames || []);
    if (frames.length === 0) {
        return values.every(v => /^[A-Za-z]{1,3}(`.*)?$/.test(v.value || ""));
    }
    return !frames.some(f => (f.filename || "").includes("/assets/"));
};

// The test build is a production build too, so NODE_ENV cannot tell them apart.
// The API it talks to can.
const environment = () => {
    if (PRODUUZIT_FRONT_ENV.NODE_ENV !== "production") return PRODUUZIT_FRONT_ENV.NODE_ENV;
    return (PRODUUZIT_FRONT_ENV.ROOT_API || "").includes("test-back") ? "test" : "production";
};

export function initSentry(app) {
    SentryCapacitor.init({
        app,
        dsn: "https://9ec9572cf66d16cf7d165a4b43696fd8@o357522.ingest.us.sentry.io/4510878674255872",
        integrations: [
            SentryCapacitor.browserTracingIntegration(),
            SentryCapacitor.replayIntegration(),
        ],
        release: "woodwall-web@" + VERSION,
        // The version no longer names the platform: the browser and Android
        // builds of one commit share it. Source maps match by debug id, so
        // dist does not affect them.
        dist: PRODUUZIT_FRONT_ENV.IS_ANDROID ? "android" : "browser",
        environment: environment(),
        sendDefaultPii: true,
        // Tracing. 10 % of transactions, which is why the ingestion figures
        // show most of them discarded here rather than sent and refused.
        tracesSampleRate: 0.1,
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/produuz\.it\/api/],

        // Session Replay: only the sessions that produced an error.
        //
        // Recording 10 % of all sessions sent about 50 000 replays a month and
        // Sentry accepted 50 of them — the other 49 950 came back
        // `rate_limited`, meaning the org's quota was already spent. What the
        // quota was spent on matters: over the same 30 days, 9 691 of 14 415
        // errors were dropped by the SDK, which backs off once the server
        // starts refusing. So recording sessions where nothing happened was
        // costing us the errors.
        //
        // 0 here plus 1.0 below keeps the replay of a session that actually
        // errored, which is the only kind anyone opens, and stops generating
        // the rest.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,

        ignoreErrors: IGNORED_MESSAGES,

        beforeSend(event, hint) {
            const e = hint?.originalException;
            if (isCanceledRequest(e)) return null;
            if (isNetworkFailure(e)) return null;
            if (isAbort(e)) return null;
            if (isForeignScript(event)) return null;
            // The router's echo of a missing chunk on an old build, which
            // utils/stale_chunks.js is already reloading or announcing.
            if (isStaleBuild() && /^Couldn't resolve component/.test(e?.message || "")) return null;
            groupAxiosError(event, e);
            return event;
        },
    },
        SentryVue.init
    );

    SentryCapacitor.setTag('version', VERSION);

    // Native crashes come from inside the WebView, and the native SDK does not
    // report which WebView it was. Tags set here are synced to the native scope.
    if (PRODUUZIT_FRONT_ENV.IS_ANDROID) {
        const webview = navigator.userAgent.match(/Chrome\/([\d.]+)/)?.[1];
        if (webview) SentryCapacitor.setTag('webview', webview);
    }
}
